import React from 'react'
import { Col, Form, notification, Row } from 'antd'
import { Formik } from 'formik'
import { number, object, setLocale } from 'yup'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { ApplicationState } from 'src/store'
import { Button, Modal } from 'src/components/Ant'
import { DatePickerRange } from 'src/components/Formik'
import { getReport142 } from 'src/services/api/report'
import { stringify } from 'src/core/hooks/use-translation'
import * as CSS from './styles'

interface Props {
  toggle: () => void
  visible: boolean
}

function getValidationSchema() {
  setLocale({
    mixed: {
      notType: 'common.required',
      required: 'common.required'
    }
  })
  return object().shape({
    ended_on: number()
      .when('started_on', (started_on: number, schema: any) =>
        schema.max(
          moment.unix(started_on).add(45, 'days').unix(),
          stringify('web.reports.parameter.period.range-error', { value: 45 })
        )
      )
      .required()
      .typeError(),
    started_on: number().required().typeError()
  })
}
function getInitialValues() {
  return {
    ended_on: moment().utc().unix(),
    started_on: moment().utc().subtract(45, 'days').unix()
  }
}

function generateCvsReduce(acc: string, cur: any) {
  return acc.concat(
    `${cur.despesa ?? ''};${cur.request_number};${
      moment(moment.unix(cur.datetrunc).utc().format('YYYYMMDD')).format(
        'DD/MM/YYYY'
      ) || '-'
    };${cur.traveler};"${cur.title}";#${cur.service}#;${cur.cost_center};${
      cur.contract
    };${cur.reason};${cur.total?.toFixed(2)};${cur.reference};${
      cur.traveler_status
    };${cur.sync_status};${cur.ticket_status};${cur.ticket_type};${
      cur.class_type
    }\n`
  )
}

function handleDownloadCsv(csvString: BlobPart, title: string) {
  const blob = new Blob([csvString], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = title
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function ModalExportOfExpenses({ visible, toggle }: Props): JSX.Element {
  const { t } = useTranslation()
  const initialValues = { ...getInitialValues() }
  const {
    me: {
      organization: { organization_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  return (
    <>
      <Modal
        cancelButtonProps={{ hidden: true }}
        destroyOnClose
        maskClosable
        okButtonProps={{ hidden: true }}
        onCancel={toggle}
        onOk={() => {}}
        title="web.side.export-of-expenses"
        visible={visible}
        width={400}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={async values => {
            const response = await getReport142({ ...values, organization_id })
            if (response.status !== 200) {
              notification.error({
                message: t('common.response.status-500'),
                placement: 'bottomRight'
              })
              return
            }

            const excelFormat = response.data.report_data.reduce(
              generateCvsReduce,
              `Despesa;Solicitação;Data;Nome do participante;Título;Serviço;Centro de custo;Contrato;Motivo;Total;Referencia;Status Request;Status integracao;Status ticket;Ticket_type;Class_type\n`
            )
            const started_on =
              moment.unix(values.started_on).format('DD-MM-YYYY') ?? ''
            const ended_on =
              moment.unix(values.ended_on).format('DD-MM-YYYY') ?? ''
            const today = moment().format('DD-MM-YYYY HH-mm-ss')
            const fileName = `toodo-despesas ${started_on} ${ended_on} ${today}.csv`
            handleDownloadCsv(excelFormat, fileName)
            notification.success({
              message: t('web.side.export-of-expenses-success'),
              placement: 'bottomRight'
            })
          }}
          render={formikProps => (
            <>
              <Form onSubmit={formikProps.handleSubmit}>
                <Row>
                  <Col span={24}>
                    <div>
                      <DatePickerRange
                        formikProps={formikProps}
                        label="web.reports.parameter.period.title"
                        name={['started_on', 'ended_on']}
                        placeholder={[
                          'web.reports.parameter.period.placeholder-start',
                          'web.reports.parameter.period.placeholder-end'
                        ]}
                        required
                        showTime={false}
                      />
                    </div>
                  </Col>
                </Row>
              </Form>
              <CSS.Div>
                <Button
                  title="common.actions.cancel"
                  type="default"
                  onClick={toggle}
                />
                <Button
                  title="web.reports.modal.action"
                  type="primary"
                  onClick={formikProps.submitForm}
                />
              </CSS.Div>
            </>
          )}
          validateOnBlur
          validationSchema={getValidationSchema()}
        />
      </Modal>
    </>
  )
}

export default ModalExportOfExpenses
